import React from "react";
import Slider from '@mui/material/Slider';
import Input from "@mui/material/Input";
import Button from "@mui/material/Button";
import { styled } from '@mui/material/styles';
import {noDataMessage, manualInput} from "./RangeSlider.styles"
import {moneyFormat} from "../../../utils/helpsers";
import PropTypes from "prop-types";

/**
 * Returns range slider component.
 * @param {[int, int]} ranges: Variable min and max range.
 * @param {[int, int]} values: The custom min/max values.
 * @param {function} onChange: Set the variable min/max ranges selected by the user.
 * @param {string} label: The descriptive label for the range slider
 * @param {[object]} markData: label, value object with reference district values related with the var.
 * @param {string} id: unique name to identify the slider on matomo.
 * @param {string} unit: the variable type unit to be displayed next to the variable value.
 * @param {boolean} collapsable: Enable/disabled the collapse fn.
 * @param {boolean} disabled.
 *
 * @returns {JSX.Element}
 */

function RangeSlider({ ranges, values, id, unit, onChange, label, markData = null, disabled = false, collapsable = false }) {
  const [value, setValue] = React.useState(values);
  const [toggle, setToggle] = React.useState(!collapsable);

  //Button styles
  const ColorButton = styled(Button)(({ theme }) => ({
    margin: "10px 0",
    color: "rgba(0, 0, 0, 0.87)",
    border: "1px solid rgba(0, 0, 0, 0.23)",
    marginBottom: '0px',
    '&:hover': {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
      border: "1px solid rgba(0, 0, 0, 0.23)",
    },
  }));

  //Format
  function valueLabelFormat(value) {
    let nf = new Intl.NumberFormat('en-US');
    const roundOut = Number.isInteger(value) ? value : value.toFixed(2);

    switch (unit) {
      case "%":
        return `${roundOut}${unit}`;
      case "$":
          if (roundOut >= 1000 && roundOut < 999999) {
              return moneyFormat({val: roundOut, base: 1000, tag: 'k'})
          } else if (roundOut >= 1000000 && roundOut < 999999999) {
              return moneyFormat({val: roundOut, base: 1000000, tag: 'M'})
          } else if (roundOut >= 1000000000) {
              return moneyFormat({val: roundOut, base: 1000000000, tag: 'B'})
          } else {
              return `${unit}${roundOut}`
          }
      default:
        return nf.format(roundOut);
    }
  }

  const handleSliderChange = (event, newValue) => {
    if (newValue[0] < newValue[1]) {
      setValue(newValue);
      onChange(newValue);
    }
    if(window._paq) {
      window._paq.push([
        "trackEvent",
        "Board DistrictExplorer",
        `Clicked: Slider ${id} - ${newValue}`,
      ]);
    }
  };

  const handleInputChange = (event, type) => {
    let inputValue = +event.target.value;
    let newValue =
      type === "min" ? [inputValue, value[1]] : [value[0], inputValue];
    setValue(newValue);
    onChange(newValue);
  };

  const handleBlur = (event) => {
    let inputValue = +event.target.value;
    if (
      inputValue < +ranges[0].toFixed(2) ||
      inputValue > +ranges[1].toFixed(2)
    ) {
      setValue([+ranges[0].toFixed(2), +ranges[1].toFixed(2)]);
      onChange([+ranges[0].toFixed(2), +ranges[1].toFixed(2)]);
    }
  };

  return typeof ranges[0] === "number" && typeof ranges[1] === "number" ? (
    <>
      <Slider
        getAriaLabel={() => label}
        value={[+value[0].toFixed(2), +value[1].toFixed(2)]}
        onChange={handleSliderChange}
        valueLabelDisplay="auto"
        valueLabelFormat={valueLabelFormat}
        getAriaValueText={() => `${value}`}
        min={+ranges[0].toFixed(2)}
        max={+ranges[1].toFixed(2)}
        step={0.1}
        classes={{
          root: "RangeSlider",
        }}
        marks={(markData && markData[0]?.value) ? [
          {
            label: `${markData[0].label}: ${valueLabelFormat(+(markData[0].value))}`,
            value: +(markData[0].value),
          }
        ] : null}
        disabled={disabled}
        sx={{
          color: "#773DBD",
          marginBottom: '4px',
          '& .MuiSlider-thumb': {
            backgroundColor: "#fff",
          },
          '& .MuiSlider-mark': {
            height: '0',
            backgroundColor: 'transparent',
            width: '0',
            zIndex: '1',
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderBottom: '14px solid #F5B335',
            marginTop: '6px',
          },
          '& .MuiSlider-mark:hover + .MuiSlider-markLabel': {
            display: 'block',
          },
          '& .MuiSlider-markLabel': {
            display: 'none',
          }
        }}
      />
      {toggle && (
        <div style={manualInput} {...toggle}>
          <Input
            className={"input__min"}
            value={[+value[0].toFixed(2)]}
            margin="dense"
            onChange={(e) => handleInputChange(e, "min")}
            onBlur={handleBlur}
            inputProps={{
              step: value[0] < 1 ? 0.1 : 1,
              min: +ranges[0].toFixed(2),
              max: +ranges[1].toFixed(2),
              type: "number",
              "aria-labelledby": "input-slider",
            }}
            disabled={disabled}
          />
          <Input
            className={"input__max"}
            value={[+value[1].toFixed(2)]}
            margin="dense"
            onChange={(e) => handleInputChange(e, "max")}
            onBlur={handleBlur}
            inputProps={{
              step: value[1] < 1 ? 0.1 : 1,
              min: +ranges[0].toFixed(2),
              max: +ranges[1].toFixed(2),
              type: "number",
              "aria-labelledby": "input-slider",
            }}
            disabled={disabled}
          />
        </div>
      )}
      {
        collapsable &&
        <ColorButton
          variant="outlined"
          onClick={() => setToggle(!toggle)}
          disabled ={disabled}
        >
          Manual Input
        </ColorButton>
      }
    </>
  ) : (
    <div style={noDataMessage} className="null-display">No Data</div>
  );
}

RangeSlider.propTypes = {
  ranges: PropTypes.array.isRequired,
  values: PropTypes.array.isRequired,
  id: PropTypes.string,
  unit: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  collapsable: PropTypes.bool,
  markData: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
  ]),
}

export default React.memo(RangeSlider);
